import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { submitEnquiry } from "@/lib/api";
import { VEHICLES, BRAND } from "@/lib/data";
import { Loader2, MessageCircle, Send } from "lucide-react";

export default function EnquiryDialog({ open, onOpenChange, defaultVehicle = "" }) {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    city: "",
    vehicle_interest: defaultVehicle,
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  // Sync vehicle selection whenever the dialog is (re)opened for a specific vehicle
  useEffect(() => {
    if (open) {
      setForm((f) => ({ ...f, vehicle_interest: defaultVehicle }));
    }
  }, [open, defaultVehicle]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) {
      toast.error("Please enter your name and phone number");
      return;
    }
    setSubmitting(true);
    try {
      await submitEnquiry({ ...form, source: "enquiry-dialog" });
      toast.success("Enquiry received! Our team will call you shortly.");
      setForm({ name: "", phone: "", email: "", city: "", vehicle_interest: defaultVehicle, message: "" });
      onOpenChange(false);
    } catch (err) {
      toast.error("Unable to submit right now. Please WhatsApp us instead.");
    } finally {
      setSubmitting(false);
    }
  };

  const whatsappHref = `https://wa.me/91${BRAND.phone}?text=${encodeURIComponent(
    `Hi Be Easy Cars, I'm interested in ${form.vehicle_interest || "a T-Permit vehicle"}. My name is ${form.name || "____"}.`
  )}`;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-[#0A1229] border-white/10 text-white sm:max-w-lg"
        data-testid="enquiry-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">
            Get a Free Consultation
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            Share your details — our commercial vehicle expert will call you
            within business hours.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 mt-2">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="enq-name" className="text-slate-300 text-xs uppercase tracking-wider">Name *</Label>
              <Input
                id="enq-name"
                data-testid="enquiry-input-name"
                value={form.name}
                onChange={(e) => update("name", e.target.value)}
                placeholder="Your full name"
                className="bg-white/5 border-white/10 text-white placeholder:text-slate-500 focus-visible:ring-red-500 focus-visible:border-red-500"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="enq-phone" className="text-slate-300 text-xs uppercase tracking-wider">Phone *</Label>
              <Input
                id="enq-phone"
                data-testid="enquiry-input-phone"
                type="tel"
                value={form.phone}
                onChange={(e) => update("phone", e.target.value)}
                placeholder="10-digit mobile"
                className="bg-white/5 border-white/10 text-white placeholder:text-slate-500 focus-visible:ring-red-500 focus-visible:border-red-500"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="enq-email" className="text-slate-300 text-xs uppercase tracking-wider">Email</Label>
              <Input
                id="enq-email"
                data-testid="enquiry-input-email"
                type="email"
                value={form.email}
                onChange={(e) => update("email", e.target.value)}
                placeholder="you@email.com"
                className="bg-white/5 border-white/10 text-white placeholder:text-slate-500 focus-visible:ring-red-500 focus-visible:border-red-500"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="enq-city" className="text-slate-300 text-xs uppercase tracking-wider">City</Label>
              <Input
                id="enq-city"
                data-testid="enquiry-input-city"
                value={form.city}
                onChange={(e) => update("city", e.target.value)}
                placeholder="Thane / Mumbai / …"
                className="bg-white/5 border-white/10 text-white placeholder:text-slate-500 focus-visible:ring-red-500 focus-visible:border-red-500"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-slate-300 text-xs uppercase tracking-wider">Vehicle of Interest</Label>
            <Select value={form.vehicle_interest} onValueChange={(v) => update("vehicle_interest", v)}>
              <SelectTrigger
                data-testid="enquiry-select-vehicle"
                className="bg-white/5 border-white/10 text-white focus:ring-red-500"
              >
                <SelectValue placeholder="Choose a model" />
              </SelectTrigger>
              <SelectContent className="bg-[#0A1229] border-white/10 text-white">
                {VEHICLES.map((v) => (
                  <SelectItem key={v.slug} value={v.name}>
                    {v.name}
                  </SelectItem>
                ))}
                <SelectItem value="Not sure — need advice">Not sure — need advice</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="enq-msg" className="text-slate-300 text-xs uppercase tracking-wider">Message</Label>
            <Textarea
              id="enq-msg"
              data-testid="enquiry-input-message"
              value={form.message}
              onChange={(e) => update("message", e.target.value)}
              placeholder="Tell us about your requirement — budget, route, timeline…"
              rows={3}
              className="bg-white/5 border-white/10 text-white placeholder:text-slate-500 focus-visible:ring-red-500 focus-visible:border-red-500"
            />
          </div>

          <div className="flex flex-col sm:flex-row gap-3 pt-2">
            <Button
              type="submit"
              disabled={submitting}
              className="flex-1 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-full"
              data-testid="enquiry-submit-btn"
            >
              {submitting ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Send className="w-4 h-4 mr-2" />
              )}
              Submit Enquiry
            </Button>
            <a
              href={whatsappHref}
              target="_blank"
              rel="noopener noreferrer"
              className="flex-1"
              data-testid="enquiry-whatsapp-btn"
            >
              <Button
                type="button"
                variant="outline"
                className="w-full border-green-500/40 bg-green-500/10 text-green-400 hover:bg-green-500/20 hover:text-green-300 rounded-full"
              >
                <MessageCircle className="w-4 h-4 mr-2" />
                WhatsApp
              </Button>
            </a>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
